"use client";

import type { ImpactPartner, Media } from "@mw/shared/payload-types";

import { Image } from "@ui/components/core/Image";
import { AspectRatio } from "@ui/components/core/AspectRatio";
import { ImageSizes } from "@ui/lib/image";
import { useTranslations } from "next-intl";
import { SwitcherButton } from "@ui/components/core/SwitcherButton";
import { useState } from "react";
import ImpactPartnerPopup from "./ImpactPartnerPopup";
import { ChevronRight } from "lucide-react";
import { CategoryName, useGetCategoryLabel } from "./ImpactPartnerGridHooks";
import { impactPartnersCategories } from "@mw/shared/impact-partners-config";

const CATEGORIES = ["all", ...impactPartnersCategories] as CategoryName[];

const Pill = ({ children }: { children: React.ReactNode }) => {
  return (
    <span className='tw-whitespace-nowrap tw-rounded-lg tw-bg-black/5 tw-px-3 tw-py-2 tw-type-sm tw-leading-none tw-tracking-normal tw-text-black [&::after]:tw-hidden [&::before]:tw-hidden'>
      {children}
    </span>
  );
};

const Sphere = () => {
  return (
    <div className='tw-flex tw-size-3 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-black/60' />
  );
};

const ImpactPartnerCard = ({ partner, onClick }: { partner: ImpactPartner; onClick: () => void }) => {
  const { description, logo, diagnosisType, type: partnerType } = partner;
  const t = useTranslations("ClientKeys.Components.impactGrid");
  return (
    <button
      className='tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-justify-end tw-space-y-[3.25rem] tw-overflow-hidden tw-rounded-xl tw-bg-gray-100 tw-p-5 tw-text-left tw-outline-none tw-transition-[background-color,box-shadow] tw-duration-300 tw-ease-out hover:tw-bg-paper hover:tw-shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)] focus-visible:tw-outline-focus lg:tw-space-y-18 lg:tw-p-7'
      type='button'
      onClick={onClick}
    >
      <div className='tw-flex tw-h-8 tw-items-center tw-justify-between'>
        {partnerType === "application" && (
          <>
            <div className='tw-flex tw-h-[1ch] tw-w-full tw-items-center tw-gap-2'>
              <Sphere />
              <span className='tw-line-clamp-1 tw-max-w-[9.375rem] tw-truncate tw-type-sm tw-font-bold tw-uppercase tw-leading-[1.1] tw-tracking-[0.05em] tw-text-gray-500 [&::after]:tw-hidden [&::before]:tw-hidden [font-family:var(--font-waldenburg-fh)]'>
                {diagnosisType}
              </span>
            </div>
            <Pill>{t("application_partner")}</Pill>
          </>
        )}
      </div>

      <div className='tw-flex tw-flex-1 tw-items-center tw-justify-center'>
        <div className='tw-relative tw-size-[7.5rem]'>
          <AspectRatio ratio='1x1' rounded={false}>
            <Image
              resource={logo as Media}
              className='tw-h-auto tw-w-full tw-object-contain'
              sizes={ImageSizes.FULL_1_3_ON_LG}
            />
          </AspectRatio>
        </div>
      </div>
      <div className='tw-flex tw-h-[2lh] tw-flex-col tw-items-end tw-justify-end'>
        <p className='tw-mt-auto tw-line-clamp-2 tw-type-base tw-leading-[1.4] tw-tracking-normal tw-text-black [&::after]:tw-hidden [&::before]:tw-hidden'>
          {description}
        </p>
      </div>
    </button>
  );
};

const ViewAllCard = ({ count, onClick }: { count: number; onClick?: () => void }) => {
  const t = useTranslations("ClientKeys.Components.impactGrid");

  return (
    <button
      className='tw-group tw-relative tw-flex tw-min-h-[18.75rem] tw-w-full tw-flex-col tw-items-center tw-justify-center tw-space-y-8 tw-overflow-hidden tw-rounded-xl tw-border tw-border-black/10 tw-bg-paper tw-p-5 tw-text-center tw-outline-none tw-transition-[background-color,box-shadow] tw-duration-300 tw-ease-out hover:tw-bg-gray-100 hover:tw-shadow-[0_0_0_1px_rgba(0,0,0,0.07),_0_2px_4px_0_rgba(0,0,0,0.04)] focus-visible:tw-outline-focus lg:tw-space-y-18 lg:tw-p-7'
      type='button'
      onClick={onClick}
    >
      <div className='tw-flex tw-h-8 tw-items-center tw-justify-between' />
      <div className='tw-flex tw-flex-1 tw-items-center tw-justify-center'>
        <div className='tw-flex tw-size-[7.5rem] tw-items-center tw-justify-center'>
          <div className='tw-flex tw-size-18 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-black/10 tw-transition-colors'>
            <ChevronRight className='tw-size-6 tw-text-black' />
          </div>
        </div>
      </div>

      <div className='tw-flex tw-h-[2lh] tw-w-full tw-flex-col tw-items-end tw-justify-end'>
        <p className='tw-w-full tw-text-left tw-type-base tw-leading-[1.4] tw-tracking-normal tw-text-black [&::after]:tw-hidden [&::before]:tw-hidden'>
          {count > 0 ? t("view_more_partners", { count }) : t("view_all_partners")}
        </p>
      </div>
    </button>
  );
};

export default function ImpactPartnerGrid({
  allPartners,
  defaultCategory = "all",
  displayPartners,
  viewAll,
}: {
  allPartners: ImpactPartner[];
  defaultCategory?: CategoryName | null;
  displayPartners: ImpactPartner[];
  viewAll: boolean;
}) {
  const gridPartners = displayPartners.length > 0 ? displayPartners : allPartners;
  const getCategoryLabel = useGetCategoryLabel();
  const categoryIndex = CATEGORIES.indexOf(defaultCategory ?? "all");
  const initialSelectedCategoryIndex = categoryIndex >= 0 ? categoryIndex : 0;
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(initialSelectedCategoryIndex);
  const [selectedPartner, setSelectedPartner] = useState<ImpactPartner | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const switcherOptions = CATEGORIES.map(cat => ({
    id: cat,
    label: getCategoryLabel(cat),
  }));
  const selectedCategory = CATEGORIES[selectedCategoryIndex];
  const displayedPartners =
    selectedCategory === "all"
      ? gridPartners
      : allPartners.filter(partner => partner.category === selectedCategory).slice(0, gridPartners.length);

  const openPartnerPopup = (partner: ImpactPartner | null) => {
    setSelectedPartner(partner);
    setIsPopupOpen(true);
  };

  return (
    <div className='space-y-20'>
      {switcherOptions.length > 1 && (
        <div className='flex items-center overflow-x-auto px-16 md:px-48'>
          <SwitcherButton
            options={switcherOptions}
            selectedIndex={selectedCategoryIndex}
            onSelect={setSelectedCategoryIndex}
            className='max-w-lg'
          />
        </div>
      )}

      <div className='tw-grid tw-grid-cols-1 tw-gap-4 tw-px-2 tw-py-2.5 md:tw-grid-cols-2 md:tw-px-4 lg:tw-grid-cols-3'>
        {displayedPartners.map(partner => (
          <ImpactPartnerCard key={partner.id} partner={partner} onClick={() => openPartnerPopup(partner)} />
        ))}
        {viewAll && (
          <ViewAllCard count={allPartners.length - gridPartners.length} onClick={() => openPartnerPopup(null)} />
        )}
      </div>

      <ImpactPartnerPopup
        partners={allPartners}
        selectedPartner={selectedPartner}
        setSelectedPartner={setSelectedPartner}
        open={isPopupOpen}
        onOpenChange={setIsPopupOpen}
      />
    </div>
  );
}
